import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  DescriptorView,
  DescriptorViewsProps,
} from '~/components/common/enriched-attribute-row';
import { useProductSearchDescriptors } from '~/features/catalog-operations/hooks/use-batch-detail';
import { hasDescriptionContent } from '~/features/catalog-operations/utils/phrase-description';
import {
  composeSearchDescriptorDescription,
  joinSearchPhrases,
} from '~/features/catalog-operations/utils/search-descriptor-description';
import type { ProductOptimization } from '~/types/enrichment';

interface Args {
  productId: string;
  /** Enrichment job id — pins the descriptor read to this batch's producer
   *  (descriptors are stored per producer, so an unpinned read could resolve a
   *  later batch's set). */
  enrichmentJobId?: string | undefined;
  /** Caller's own readiness gate (dialog open, detail resolved, feed source…). */
  enabled: boolean;
  /** The run turned search descriptors off — no fetch, and no toggle at all. */
  searchDescriptorsDisabled: boolean;
}

/**
 * Description / Search Descriptors / Phrase Descriptions toggle for the Product
 * Description row, shared by the product detail page and the quick view so both
 * render the same views. Owns the descriptor fetch and the active view (reset
 * per product); `descriptorViewsFor(opt)` yields the row's `descriptorViews`
 * props for the description field and `undefined` for every other row.
 */
export function useDescriptionDescriptorViews({
  productId,
  enrichmentJobId,
  enabled,
  searchDescriptorsDisabled,
}: Args) {
  const { t } = useTranslation('catalog-operations');
  const [active, setActive] = useState<DescriptorView>('description');
  // Resets on every product change so paging to a sibling never carries over a
  // view the new product may not have data for. Adjusted during render (React's
  // "reset state when a prop changes" pattern) rather than in an effect.
  const [activeProductId, setActiveProductId] = useState(productId);
  if (productId !== activeProductId) {
    setActiveProductId(productId);
    setActive('description');
  }

  const query = useProductSearchDescriptors(
    productId,
    enabled && !searchDescriptorsDisabled,
    enrichmentJobId,
  );
  const { data, isLoading, isError } = query;

  const phrasesJoined = joinSearchPhrases(data);
  const phrase = data?.phraseDescription ?? null;
  // Judged on each view's own resolved data — never disabled while the query is
  // in flight or has failed, since that's transient rather than a product fact.
  const settled = !isLoading && !isError;
  const searchDescriptorsDisabledState =
    settled && (data?.searchPhrases.length ?? 0) === 0
      ? { tooltip: t('productDescriptionViews.noSearchDescriptorsTooltip') }
      : (false as const);
  const phraseDescriptionsDisabledState =
    settled && !(phrase && hasDescriptionContent(phrase))
      ? { tooltip: t('productDescriptionViews.noPhraseDescriptionsTooltip') }
      : (false as const);

  /** The description row's toggle props; `undefined` for other rows, and for
   *  every row when the run never enabled search descriptors. */
  function descriptorViewsFor(opt: ProductOptimization): DescriptorViewsProps | undefined {
    if (opt.field !== 'description' || searchDescriptorsDisabled) return undefined;
    return {
      active,
      onActiveChange: setActive,
      // `<default description> | <phrases>` — appended to the pre-enrichment
      // copy, not the enriched one.
      searchDescriptorsValue: phrasesJoined
        ? composeSearchDescriptorDescription(opt.currentValue, phrasesJoined)
        : opt.value,
      searchDescriptorsDisabled: searchDescriptorsDisabledState,
      // Level 3 (the rewrite with phrases) only, against Current on the left.
      phraseDescriptionsValue: phrase?.level3Description?.trim() ?? '',
      phraseDescriptionsError: phrase?.level3Error ?? undefined,
      phraseDescriptionsDisabled: phraseDescriptionsDisabledState,
    };
  }

  return { query, descriptorViewsFor };
}
